// «ذخیره‌شده‌ها» — روی همین گوشی، و اگر کاربر وارد شده باشد روی حسابش هم (جدولِ saved_designs روی سرور، فقط
// لینکِ طرح‌ها) تا با عوض کردنِ گوشی یا نصبِ دوباره از بین نروند. بعد از ورود، ذخیره‌های قبلیِ گوشی به حساب
// اضافه می‌شوند و فهرستِ حساب روی گوشی می‌آید.
//
// کجا نگه داشته می‌شود:
//   ۱) حافظه‌ی خودِ برنامه (localStorage) — با درخواستِ «ماندگاری» (navigator.storage.persist) تا مرورگر خودش
//      پاکش نکند. روی اندروید با حذف/نصبِ دوباره‌ی برنامه معمولاً می‌ماند (حافظه مالِ سایت است، نه آیکون).
//   ۲) فایلِ پشتیبان روی خودِ گوشی: khoonero-saved.json (در «دانلودها»/«Files»). روی آیفون حذفِ برنامه از صفحه‌ی
//      اصلی حافظه‌اش را پاک می‌کند؛ بعد از نصبِ دوباره، اگر ذخیره‌ها خالی بود، برنامه همین فایل را می‌خواهد و
//      همه‌چیز برمی‌گردد. مرورگر اجازه نمی‌دهد سایت بی‌اجازه فایلی از گوشی بخواند — انتخابِ فایل با خودِ کاربر است.

import { fetchSavedDesigns, putSavedDesign, deleteSavedDesign, mergeSavedDesigns } from './designsApi.jsx';

const KEY = 'kh-saved-v1';
const BACKUP_KEY = 'kh-saved-backup-at'; // آخرین پشتیبان‌گیری (برای یادآوری)
export const BACKUP_FILE = 'khoonero-saved.json';
const listeners = new Set();

function read() {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(v) ? v.filter((x) => x && typeof x.id === 'string') : [];
  } catch (e) {
    return [];
  }
}
function write(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch (e) {
    /* حافظه پر/حالتِ خصوصی */
  }
  listeners.forEach((fn) => fn(list));
}

let persistAsked = false;
function askPersist() {
  if (persistAsked) return;
  persistAsked = true;
  try {
    if (navigator.storage && navigator.storage.persist) navigator.storage.persist().catch(() => {});
  } catch (e) {
    /* */
  }
}

// فقط چیزهایی که برای نمایش لازم است (نامِ خصوصیِ طرح هرگز)
function snapshot(d) {
  return {
    id: d.id,
    listing: d.listing || null,
    thumbUrl: d.thumbUrl || null,
    filmUrl: d.filmUrl || null,
    filmSmUrl: d.filmSmUrl || null,
    owner: d.owner || null,
    views: d.views || 0,
    createdAt: d.createdAt || null,
    // برای «ورود» به خانه (سه‌بعدی)
    planUrl: d.planUrl || null,
    modelUrl: d.modelUrl || null,
    lightmapUrl: d.lightmapUrl || null,
    lightmapIntensity: d.lightmapIntensity || null,
  };
}

// حسابِ واردشده (null = فقط گوشی)
let account = null;
const remote = (fn) => {
  if (!account) return;
  fn().catch(() => {
    /* بارِ بعدی که برنامه باز شود، با merge جبران می‌شود */
  });
};

// بعد از ورود (یا باز شدنِ برنامه با حسابِ واردشده): ذخیره‌های گوشی به حساب اضافه و فهرستِ حساب جایگزین می‌شود.
export async function syncSavedWithAccount(user) {
  if (!user) {
    // خروج از حساب: ذخیره‌ها روی حساب امن‌اند؛ روی گوشی پاک می‌شوند تا به حسابِ نفرِ بعدی (گوشیِ مشترک) نروند
    if (account) write([]);
    account = null;
    return;
  }
  account = user.id;
  const me = account;
  try {
    const local = read();
    const r = local.length
      ? await mergeSavedDesigns(local.map((x) => ({ id: x.id, savedAt: x.savedAt })))
      : await fetchSavedDesigns();
    if (account !== me) return;
    const items = (r.items || []).map((d) => ({ ...snapshot(d), savedAt: d.savedAt || new Date().toISOString() }));
    write(items.slice(0, 500));
  } catch (e) {
    /* بدونِ اینترنت: همان فهرستِ گوشی */
  }
}

export const getSaved = () => read();
export const isSaved = (id) => read().some((x) => x.id === id);

export function toggleSaved(design) {
  askPersist();
  const list = read();
  const i = list.findIndex((x) => x.id === design.id);
  if (i >= 0) {
    list.splice(i, 1);
    write(list);
    remote(() => deleteSavedDesign(design.id));
    return false;
  }
  list.unshift({ ...snapshot(design), savedAt: new Date().toISOString() });
  write(list.slice(0, 500));
  remote(() => putSavedDesign(design.id));
  return true;
}

export function removeSaved(id) {
  write(read().filter((x) => x.id !== id));
  remote(() => deleteSavedDesign(id));
}

export const isAccountSynced = () => Boolean(account);

export function subscribeSaved(fn) {
  listeners.add(fn);
  const onStorage = (e) => {
    if (e.key === KEY) fn(read());
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(fn);
    window.removeEventListener('storage', onStorage);
  };
}

// چند موردِ ذخیره‌شده هنوز در فایلِ پشتیبان نیست
export function unsavedSinceBackup() {
  let at = '';
  try {
    at = localStorage.getItem(BACKUP_KEY) || '';
  } catch (e) {
    /* */
  }
  return read().filter((x) => !at || (x.savedAt || '') > at).length;
}

// فایلِ پشتیبان روی خودِ گوشی
export async function exportBackup() {
  const list = read();
  const blob = new Blob([JSON.stringify({ app: 'khoonero', kind: 'saved', version: 1, exportedAt: new Date().toISOString(), items: list })], {
    type: 'application/json',
  });
  const file = typeof File !== 'undefined' ? new File([blob], BACKUP_FILE, { type: 'application/json' }) : null;
  let done = false;
  // روی موبایل «اشتراک‌گذاریِ فایل» گزینه‌ی «ذخیره در Files/فایل‌ها» را هم دارد
  if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: 'پشتیبانِ ذخیره‌های خونه‌رو' });
      done = true;
    } catch (e) {
      if (e && e.name === 'AbortError') return false;
    }
  }
  if (!done) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = BACKUP_FILE;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }
  try {
    localStorage.setItem(BACKUP_KEY, new Date().toISOString());
  } catch (e) {
    /* */
  }
  listeners.forEach((fn) => fn(read()));
  return true;
}

// بازگرداندن از فایل (با ذخیره‌های فعلی ادغام می‌شود) → تعدادِ موردهای تازه
export async function importBackup(file) {
  const text = await file.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    throw new Error('این فایل، فایلِ پشتیبانِ خونه‌رو نیست.');
  }
  const items = data && data.app === 'khoonero' && Array.isArray(data.items) ? data.items : null;
  if (!items) throw new Error('این فایل، فایلِ پشتیبانِ خونه‌رو نیست.');
  const list = read();
  const have = new Set(list.map((x) => x.id));
  let added = 0;
  items.forEach((x) => {
    if (x && typeof x.id === 'string' && /^[A-Za-z0-9_-]{8,24}$/.test(x.id) && !have.has(x.id)) {
      list.push({ ...snapshot(x), savedAt: x.savedAt || new Date().toISOString() });
      have.add(x.id);
      added += 1;
    }
  });
  list.sort((a, b) => String(b.savedAt).localeCompare(String(a.savedAt)));
  write(list.slice(0, 500));
  remote(() => mergeSavedDesigns(list.map((x) => ({ id: x.id, savedAt: x.savedAt }))));
  askPersist();
  try {
    localStorage.setItem(BACKUP_KEY, new Date().toISOString());
  } catch (e) {
    /* */
  }
  return added;
}
